import { Link } from 'react-router-dom';
import { Button } from '@roger/ui-components';
import {
  orderManagerTabForStatus,
  ordersHighlightPath,
} from '../../utils/navigation';
import type { FaxItem } from '../../services/faxItems';

interface OrderAnswerLineProps {
  item: FaxItem;
  deciding: boolean;
  archiving: boolean;
  saving: boolean;
  /** True while the loaded row is not yet the one in the URL. */
  disabled?: boolean;
  decideSignature: (signed: boolean) => void;
  toggleArchive: () => void;
}

/**
 * A signed order that came back knows which order it answers, because the
 * mark on it said so. Linking it saves hunting for that order by patient and
 * date.
 */
export default function OrderAnswerLine({
  item,
  deciding,
  archiving,
  saving,
  disabled = false,
  decideSignature,
  toggleArchive,
}: OrderAnswerLineProps) {
  if (!item.answers_order_id) return null;
  return (
    <div className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
      <span>
        {item.kind === 'document' ? 'Part of' : 'Answers'}{' '}
        <Link
          className='font-medium text-primary underline underline-offset-2'
          to={ordersHighlightPath(
            item.answers_order_id,
            // Named from the order's own status rather than assumed: answering
            // the question moves it to another tab.
            item.answers_order_status
              ? orderManagerTabForStatus(item.answers_order_status)
              : null
          )}
        >
          Order #{item.answers_order_id.slice(0, 8)}
        </Link>
        .
      </span>
      {/* Asked here because this is the one screen showing the page. A mark
          says which order it belongs to and nothing about a signature. */}
      {item.kind === 'arrival' && item.answers_order_awaiting_decision && (
        <span className='flex items-center gap-2'>
          <span>Is it signed?</span>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={deciding || saving || disabled}
            onClick={() => decideSignature(false)}
          >
            Not signed
          </Button>
          <Button
            type='button'
            size='sm'
            disabled={deciding || saving || disabled}
            onClick={() => decideSignature(true)}
          >
            Confirm signed
          </Button>
        </span>
      )}
      {/* Already on its order, so it needs taking out of the list, not filing. */}
      {!item.chart_filed_at && (
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={archiving || saving || disabled}
          onClick={toggleArchive}
        >
          {item.archived_at ? 'Move back to inbox' : 'Archive'}
        </Button>
      )}
    </div>
  );
}
